import React from 'react';
import type { UserListQuery } from '@/api/handlers/resources/users/requests';
import type { ErrorResponse } from '@/api/handlers/responses';
import type { UpdateUserRequest } from '@/api/handlers/resources/users/requests';
import type { UserRole } from '@/api/models';
import {
    ALL_BRANCHES_WITH_ARCHIVED_QUERY,
    useBranchDetailsQuery,
    useDeleteUserMutation,
    useSetUserPasswordMutation,
    useSetUserPinMutation,
    useUpdateUserMutation,
    useUsersQuery,
} from '@/api/queries';
import { getUser } from '@/utils';
import { useCreateUser } from './useCreateUser';
import {
    TEAM_PAGE_SIZE,
    canReassignBranchOrRole,
    getRoleFields,
    shouldShowBranchColumn,
    type User,
} from './components/teamMeta';
import type {
    BranchOption,
    BranchRef,
    UserFormInitialValues,
    UserFormSubmit,
} from './components/userFormModal';

/** Debounce window for the search box before a new request fires. */
const SEARCH_DEBOUNCE_MS = 300;

export type UseTeamPageResult = {
    data: User[] | null;
    error: ErrorResponse | null;
    isLoading: boolean;
    onRetry: () => void;
    // search + pagination
    search: string;
    onSearchChange: (value: string) => void;
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    onPageSizeChange: (pageSize: number) => void;
    // caller-derived view config
    callerRole: UserRole;
    callerBranchId: string | null;
    callerId: string | null;
    showBranchColumn: boolean;
    branchOptions: BranchOption[];
    /**
     * The branch a user is assigned to, however that branch is doing —
     * including archived, which the picker list deliberately excludes. Null
     * when the user is org-wide; a name with `archived` set when the branch has
     * been taken out of service. Never a raw id.
     */
    branchRefById: (id: string | null) => BranchRef | null;
    // create / edit modal
    isFormOpen: boolean;
    formMode: 'create' | 'edit';
    formError: string | null;
    isFormSubmitting: boolean;
    isEditingSelf: boolean;
    editingInitialValues: UserFormInitialValues | null;
    onAddUser: () => void;
    onEditUser: (id: string) => void;
    onCloseForm: () => void;
    onSubmitUser: (input: UserFormSubmit) => void;
    // PIN modal
    isPinOpen: boolean;
    pinError: string | null;
    isPinSubmitting: boolean;
    onOpenPin: (id: string) => void;
    onClosePin: () => void;
    onSubmitPin: (pin: string) => void;
    // password modal
    isPasswordOpen: boolean;
    passwordError: string | null;
    isPasswordSubmitting: boolean;
    onOpenPassword: (id: string) => void;
    onClosePassword: () => void;
    onSubmitPassword: (password: string) => void;
    // delete
    onDeleteUser: (id: string) => void;
};

/**
 * Drives the Team page. The list is **server-paginated** (`GET /users` takes
 * page / pageSize / search and returns the matching page plus totalCount /
 * totalPages), so search and paging are pushed to the API rather than done
 * client-side — the whole user set is never loaded at once. The server
 * auto-scopes a BranchAdmin to their branch.
 *
 * Beyond listing, the hook resolves branch names from the branches cache and
 * orchestrates the create/edit, set-PIN, reset-password, and delete flows.
 * Caller role/branch/id (from the session) gate what's offered.
 */
export const useTeamPage = (): UseTeamPageResult => {
    const caller = getUser();
    const callerRole: UserRole = caller?.role ?? 'Seller';
    const callerBranchId = caller?.branchId ?? null;
    const callerId = caller?.id ?? null;

    const [page, setPage] = React.useState(1);
    const [pageSize, setPageSize] = React.useState(TEAM_PAGE_SIZE);
    const [search, setSearch] = React.useState('');
    const [debouncedSearch, setDebouncedSearch] = React.useState('');

    const [isFormOpen, setIsFormOpen] = React.useState(false);
    const [editingId, setEditingId] = React.useState<string | null>(null);
    const [formError, setFormError] = React.useState<string | null>(null);
    const [pinTargetId, setPinTargetId] = React.useState<string | null>(null);
    const [pinError, setPinError] = React.useState<string | null>(null);
    const [passwordTargetId, setPasswordTargetId] = React.useState<
        string | null
    >(null);
    const [passwordError, setPasswordError] = React.useState<string | null>(
        null,
    );

    // Debounce the term that actually drives the request so we don't fire a
    // query per keystroke.
    React.useEffect(() => {
        const handle = setTimeout(
            () => setDebouncedSearch(search),
            SEARCH_DEBOUNCE_MS,
        );
        return () => clearTimeout(handle);
    }, [search]);

    // A new search starts from page 1.
    React.useEffect(() => {
        setPage(1);
    }, [debouncedSearch]);

    const query: UserListQuery = {
        page,
        pageSize,
        search: debouncedSearch.trim() || undefined,
    };
    const usersQuery = useUsersQuery(query);
    // Two reads of the same endpoint, for two different questions. The picker
    // list answers "which branch can this user be assigned to", so it excludes
    // archived. Name resolution answers "what is the branch this user is
    // already on called", and an archived branch has an answer to that — the
    // Team page used to fall back to the raw id (bos-api BOS-122).
    const branchesQuery = useBranchDetailsQuery();
    const allBranchesQuery = useBranchDetailsQuery(
        ALL_BRANCHES_WITH_ARCHIVED_QUERY,
    );
    const { create, isCreating } = useCreateUser();
    const updateMutation = useUpdateUserMutation();
    const setPinMutation = useSetUserPinMutation();
    const setPasswordMutation = useSetUserPasswordMutation();
    const deleteMutation = useDeleteUserMutation();

    const response =
        usersQuery.data && usersQuery.data.success
            ? usersQuery.data.response
            : null;

    const items: User[] | null = response?.items ?? null;
    const data: User[] | null = items;

    const error: ErrorResponse | null =
        usersQuery.data && !usersQuery.data.success
            ? usersQuery.data.error
            : null;

    const branchOptions: BranchOption[] = React.useMemo(() => {
        if (!branchesQuery.data || !branchesQuery.data.success) {
            return [];
        }
        return branchesQuery.data.response.items.map((b) => ({
            id: b.id,
            name: b.name,
        }));
    }, [branchesQuery.data]);

    const branchRefsById = React.useMemo<Record<string, BranchRef>>(() => {
        if (!allBranchesQuery.data || !allBranchesQuery.data.success) {
            return {};
        }
        return Object.fromEntries(
            allBranchesQuery.data.response.items.map((b) => [
                b.id,
                { id: b.id, name: b.name, archived: b.archived },
            ]),
        );
    }, [allBranchesQuery.data]);

    const branchRefById = React.useCallback(
        (id: string | null): BranchRef | null => {
            if (!id) {
                return null;
            }
            // An id with no row behind it still renders — RefLabel turns it
            // into "Unknown (a1b2c3d4…)" rather than a bare GUID.
            return branchRefsById[id] ?? { id, name: null, archived: false };
        },
        [branchRefsById],
    );

    const editingRow = React.useMemo(() => {
        if (editingId === null || items === null) {
            return null;
        }
        return items.find((u) => u.id === editingId) ?? null;
    }, [editingId, items]);

    const editingInitialValues: UserFormInitialValues | null =
        React.useMemo(() => {
            if (editingRow === null) {
                return null;
            }
            return {
                name: editingRow.name,
                role: editingRow.role,
                branchId: editingRow.branchId,
                status: editingRow.status,
            };
        }, [editingRow]);

    const onSearchChange = React.useCallback((v: string) => setSearch(v), []);

    const onPageChange = React.useCallback((next: number) => {
        setPage(next);
    }, []);

    const onPageSizeChange = React.useCallback((next: number) => {
        setPageSize(next);
        setPage(1);
    }, []);

    const onRetry = React.useCallback(() => {
        usersQuery.refetch();
    }, [usersQuery]);

    const onAddUser = React.useCallback(() => {
        setEditingId(null);
        setFormError(null);
        setIsFormOpen(true);
    }, []);

    const onEditUser = React.useCallback((id: string) => {
        setEditingId(id);
        setFormError(null);
        setIsFormOpen(true);
    }, []);

    const onCloseForm = React.useCallback(() => {
        setIsFormOpen(false);
        setEditingId(null);
        setFormError(null);
    }, []);

    const onSubmitUser = React.useCallback(
        async (input: UserFormSubmit) => {
            setFormError(null);

            if (editingId === null) {
                // The request body is assembled from the role matrix, and that
                // assembly is shared with the Control Center's "Add user",
                // which opens this same form from the launcher — see
                // `useCreateUser`.
                const created = await create(input);
                if (!created.ok) {
                    setFormError(created.message);
                    return;
                }
                setIsFormOpen(false);
                return;
            }

            // Edit: name always. Status + role/branch are withheld when editing
            // your own row so you can't disable or demote yourself into a
            // lockout. Role/branch reassignment is otherwise OrgAdmin-only; when
            // the new role has no branch (OrgAdmin) we send branchId: null to
            // explicitly clear the old assignment rather than leave it stale.
            const isSelf = editingId === callerId;
            const body: UpdateUserRequest = { name: input.name };
            if (!isSelf) {
                body.status = input.status;
                if (canReassignBranchOrRole(callerRole)) {
                    body.role = input.role;
                    body.branchId = getRoleFields(input.role).branch
                        ? input.branchId
                        : null;
                }
            }
            const result = await updateMutation.mutateAsync({
                id: editingId,
                body,
            });
            if (!result.success) {
                setFormError(result.error.message ?? 'Failed to update user.');
                return;
            }
            setEditingId(null);
            setIsFormOpen(false);
        },
        [callerId, callerRole, create, editingId, updateMutation],
    );

    const onOpenPin = React.useCallback((id: string) => {
        setPinTargetId(id);
        setPinError(null);
    }, []);
    const onClosePin = React.useCallback(() => {
        setPinTargetId(null);
        setPinError(null);
    }, []);
    const onSubmitPin = React.useCallback(
        async (pin: string) => {
            if (pinTargetId === null) {
                return;
            }
            setPinError(null);
            const result = await setPinMutation.mutateAsync({
                id: pinTargetId,
                pin,
            });
            if (!result.success) {
                setPinError(result.error.message ?? 'Failed to set PIN.');
                return;
            }
            setPinTargetId(null);
        },
        [pinTargetId, setPinMutation],
    );

    const onOpenPassword = React.useCallback((id: string) => {
        setPasswordTargetId(id);
        setPasswordError(null);
    }, []);
    const onClosePassword = React.useCallback(() => {
        setPasswordTargetId(null);
        setPasswordError(null);
    }, []);
    const onSubmitPassword = React.useCallback(
        async (password: string) => {
            if (passwordTargetId === null) {
                return;
            }
            setPasswordError(null);
            const result = await setPasswordMutation.mutateAsync({
                id: passwordTargetId,
                password,
            });
            if (!result.success) {
                setPasswordError(
                    result.error.message ?? 'Failed to reset password.',
                );
                return;
            }
            setPasswordTargetId(null);
        },
        [passwordTargetId, setPasswordMutation],
    );

    const onDeleteUser = React.useCallback(
        (id: string) => {
            // The row action is hidden for the caller's own row; guard anyway.
            if (id === callerId) {
                return;
            }
            deleteMutation.mutate(id);
        },
        [callerId, deleteMutation],
    );

    return {
        data,
        error,
        isLoading: usersQuery.isLoading,
        onRetry,
        search,
        onSearchChange,
        page,
        pageSize,
        totalCount: response?.totalCount ?? 0,
        totalPages: response?.totalPages ?? 1,
        onPageChange,
        onPageSizeChange,
        callerRole,
        callerBranchId,
        callerId,
        showBranchColumn: shouldShowBranchColumn(callerRole),
        branchOptions,
        branchRefById,
        isFormOpen,
        formMode: editingId === null ? 'create' : 'edit',
        formError,
        isFormSubmitting: isCreating || updateMutation.isPending,
        isEditingSelf: editingId !== null && editingId === callerId,
        editingInitialValues,
        onAddUser,
        onEditUser,
        onCloseForm,
        onSubmitUser,
        isPinOpen: pinTargetId !== null,
        pinError,
        isPinSubmitting: setPinMutation.isPending,
        onOpenPin,
        onClosePin,
        onSubmitPin,
        isPasswordOpen: passwordTargetId !== null,
        passwordError,
        isPasswordSubmitting: setPasswordMutation.isPending,
        onOpenPassword,
        onClosePassword,
        onSubmitPassword,
        onDeleteUser,
    };
};
